import React from 'react';

/**
 * Reusable Button Component.
 * Styles with Tailwind CSS, supporting variants, sizes, and focus states.
 *
 * @param {object} props - Component props.
 * @param {string} [props.variant='primary'] - Button style variant ('primary', 'secondary', 'danger', 'outline', 'ghost').
 * @param {string} [props.size='md'] - Button size ('sm', 'md', 'lg').
 * @param {boolean} [props.fullWidth=false] - If true, button takes full width.
 * @param {string} [props.className=''] - Additional custom Tailwind classes.
 * @param {React.ReactNode} props.children - Button content.
 * @param {function} [props.onClick] - Click event handler.
 * @param {string} [props.type='button'] - Button type ('button', 'submit', 'reset').
 * @param {boolean} [props.disabled=false] - If true, button is disabled.
 */
const Button = ({
    children,
    onClick,
    type = 'button',
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    disabled = false,
    className = '',
    ...props
}) => {
    // Base styles for all buttons
    const baseStyles = 'font-semibold rounded-lg transition-all duration-200 ease-in-out ' +
        'focus:outline-none focus:ring-2 focus:ring-offset-2 ' +
        'disabled:opacity-50 disabled:cursor-not-allowed';

    // Variant-specific styles
    const variantStyles = {
        primary: 'bg-gmail-blue text-white hover:bg-blue-600 focus:ring-gmail-blue',
        secondary: 'bg-gray-medium text-text-primary hover:bg-gray-dark focus:ring-gray-dark',
        danger: 'bg-gmail-red text-white hover:bg-red-600 focus:ring-gmail-red',
        outline: 'border border-gray-dark text-text-primary hover:bg-gray-light focus:ring-gray-dark',
        ghost: 'text-text-primary hover:bg-gray-light focus:ring-gray-dark',
        indigo: 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500',
        indigolight: 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 focus:ring-indigo-500',
    };

    // Size-specific styles
    const sizeStyles = {
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-4 py-2 text-base',
        lg: 'px-5 py-2.5 text-lg',
    };

    const widthStyle = fullWidth ? 'w-full' : 'w-auto';

    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${widthStyle} ${className}`}
            {...props}
        >
            {children}
        </button>
    );
};

export default Button;
